import React, { useEffect, useState } from 'react';
import { sprintf } from 'sprintf-js';

import { CurrentUser } from 'api/currentUser';
import Box from 'components/Box';
import Form, {
  FileUploadField,
  InputField,
  MultiInputField,
  RecaptchaField,
  Section,
  SelectField,
  SubmitButton,
  TextAreaField,
} from 'components/Form';
import { BodySmall, H3 } from 'components/Text';
import { LOCALES } from 'utils/constants';
import { postJson } from 'utils/http';
import { resetRecaptcha } from 'utils/recaptcha';

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FormValues = Record<string, any>;
type Reason = {
  id: string;
  text: string;
  subReasons?: Array<Reason>;
};
const ATTACHMENTS_MAX_LENGTH = 5;

const handleSubmit = (
  { handleError, updateSubmitting, values },
  blockId,
  currentUser,
  setSubmittedValues: (values: FormValues | null | undefined) => void
) => {
  const { orgs = [] } = currentUser || {};
  const { id: orgId = '' } =
    orgs.find((org) => org.name === values.orgName) || {};
  const valuesToSubmit = {
    orgId,
    ...values,
    attachmentIds: values.attachmentIds.filter(Boolean),
  };
  postJson(`/data/website/support-contact-form/${blockId}`, valuesToSubmit)
    .then(() => {
      updateSubmitting(false);
      setSubmittedValues(valuesToSubmit);
    })
    .catch((e) => {
      resetRecaptcha();
      handleError(e, {
        recaptchaKey: values.recaptchaKey + 1,
        gRecaptchaResponse: undefined,
      });
    });
};

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
const SupportContactForm = ({
  currentUser,
  reasons,
  selectedReason,
  selectedSubReason,
  updateValue,
}: {
  currentUser: CurrentUser | null | undefined;
  reasons: Array<Reason>;
  selectedReason: Reason | null | undefined;
  selectedSubReason: Reason | null | undefined;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateValue: (...args: Array<any>) => any;
}) => {
  const [subReasons, setSubReasons] = useState([]);
  const [subSubReasons, setSubSubReasons] = useState([]);
  useEffect(() => {
    if (selectedReason) {
      setSubReasons(selectedReason.subReasons || []);

      if (selectedReason.id === 'ORG' && selectedSubReason) {
        setSubSubReasons(selectedSubReason.subReasons || []);
      } else {
        setSubSubReasons([]);
        updateValue('subSubReason', undefined);
      }
    }
  }, [selectedReason, selectedSubReason, updateValue]);
  return (
    <>
      <InputField label={`${getText('Email to contact you')}*`} name="email" />
      <InputField label={`${getText('First Name')}*`} name="firstName" />
      <InputField label={`${getText('Last name')}*`} name="lastName" />
      <SelectField
        label={`${getText('Your language')}*`}
        name="locale"
        options={LOCALES.map((locale) => ({
          label: locale.name,
          value: locale.id,
        }))}
      />
      {currentUser && currentUser.orgs && currentUser.orgs.length > 0 ? (
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <>
          <SelectField
            label={`${getText('Which organization is this about?')}*`}
            name="orgName"
            options={[
              {
                label: `${getText('This is not about an organization')}`,
                value: '',
              },
            ].concat(
              currentUser.orgs.map((org) => ({
                label: org.name,
                value: org.name,
              }))
            )}
          />
        </>
      ) : (
        <InputField
          label={`${getText(
            'Which organization is this regarding (if any)?'
          )}*`}
          name="orgName"
        />
      )}
      <SelectField
        label={`${getText('What is this about?')}*`}
        name="reason"
        options={[
          {
            label: `${getText('Please Select')}`,
            value: '',
          },
        ].concat(
          reasons.map((reason) => ({
            label: reason.text,
            value: reason.id,
          }))
        )}
      />
      {subReasons.length > 0 && (
        <SelectField
          label={`${getText('Narrow it down')}*`}
          name="subReason"
          options={[
            {
              label: `${getText('Please select')}`,
              value: '',
            },
          ].concat(
            subReasons.map((subReason) => ({
              label: subReason.text,
              value: subReason.id,
            }))
          )}
        />
      )}
      {subSubReasons.length > 0 && (
        <SelectField
          label={`${getText('Narrow it down')}*`}
          name="subSubReason"
          options={[
            {
              label: `${getText('Please select')}`,
              value: '',
            },
          ].concat(
            subSubReasons.map((subSubReason) => ({
              label: subSubReason.text,
              value: subSubReason.id,
            }))
          )}
        />
      )}
      <TextAreaField
        label={
          selectedReason && selectedReason.id === 'FEEDBACK'
            ? getText('Please describe your feedback in detail')
            : getText('How can we help?')
        }
        name="message"
      />

      <MultiInputField
        hideControls
        component={FileUploadField}
        maxInputs={ATTACHMENTS_MAX_LENGTH}
        name="attachmentIds"
        label={getText('Attachments')}
        description={sprintf(getText('Upload up to %(count)s attachments'), {
          count: ATTACHMENTS_MAX_LENGTH,
        })}
        extraData={new Array(ATTACHMENTS_MAX_LENGTH).fill({
          downloadAttachment: 'false',
          fileType: 'CONTACT_FORM',
          isRequired: false,
          label: '',
        })}
        extraAnalyticsData={new Array(ATTACHMENTS_MAX_LENGTH).fill({
          context: 'Support Contact Form',
          file_label: 'Attachments',
        })}
        showEmpty
      />
      <RecaptchaField />

      <Box mt={64}>
        <SubmitButton size="large" width={1}>
          {getText('Submit')}
        </SubmitButton>
      </Box>
    </>
  );
};

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/function-component-definition
const SupportContactFormBlock = ({
  blockId,
  currentUser,
  data,
}: {
  blockId: string;
  currentUser: CurrentUser | null | undefined;
  data: {
    reasons: Array<Reason>;
  };
}) => {
  const [submittedValues, setSubmittedValues] = useState<
    FormValues | null | undefined
  >();
  const { reasons } = data;
  const submitedWithReasonFeedback =
    submittedValues && submittedValues.reason === 'FEEDBACK';
  return submittedValues ? (
    <Box m={64} textAlign="center">
      <H3>{getText('Thanks for submitting')}</H3>
      <BodySmall>
        {submitedWithReasonFeedback
          ? getText(
              'We’ll make sure to consider your feedback and will get back to you when we can!'
            )
          : getText('Someone will respond to you shortly.')}
      </BodySmall>
    </Box>
  ) : (
    <Section>
      <Form
        data-qa-id="support-contact-form"
        initialValues={{
          email: currentUser && currentUser.email,
          firstName: currentUser && currentUser.firstName,
          lastName: currentUser && currentUser.lastName,
          locale: USER_LOCALE,
          attachmentIds: [''],
        }}
        onSubmit={(formContext) =>
          // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
          handleSubmit(formContext, blockId, currentUser, setSubmittedValues)
        }
        render={({ getValue, updateValue }) => {
          const selectedReason = reasons.find(
            (r) => r.id === getValue('reason')
          );
          const selectedSubReason =
            selectedReason &&
            selectedReason.subReasons &&
            selectedReason.subReasons.find(
              (s) => s.id === getValue('subReason')
            );
          return (
            <SupportContactForm
              // @ts-expect-error ts-migrate(2322) FIXME: Type '{ blockId: string; currentUser: CurrentUser;... Remove this comment to see the full error message
              blockId={blockId}
              currentUser={currentUser}
              reasons={reasons}
              selectedReason={selectedReason}
              selectedSubReason={selectedSubReason}
              updateValue={updateValue}
            />
          );
        }}
      />
    </Section>
  );
};

export default SupportContactFormBlock;
